import { fetchTeams, stopTeamsPolling, type FetchTeamsResult } from "./fetchTeams";
import { parseSportData, type SportJsonInput } from "../utils/parseSportData";
import { parseCompetitionsData, type CompetitionsJsonInput } from "../utils/parseCompetitionsData";
import { parseCompetitionTypeData, type CompetitionTypeJsonInput } from "../utils/parseCompetitionTypeData";
import { setSportsMap, setCompetitionsMap, setCompetitionTypeMap } from "./useCuiConfig";

const BASE_URL = "https://api.americanwagering.com/caesars-ui-assets/json";
const DEFAULT_POLL_INTERVAL = 86400000; // 24 hours
const MIN_POLL_INTERVAL = 5000;
const FETCH_TIMEOUT = 30000;

const DEFAULT_HEADERS: HeadersInit = {
  "X-Platform": "cordova-desktop",
  "X-App-Version": "1.0.0",
  "X-Unique-Device-Id": "cui-fetch",
};

type ResourceKey = "teams" | "sport" | "competitions" | "competitionType";

export type FetchCUIDataOptions = {
  resources?: ResourceKey[];
  baseUrl?: string;
  forceUpdate?: boolean;
  polling?: boolean;
  interval?: number;
};

export type FetchResult = {
  success: boolean;
  updated: boolean;
  error?: string;
};

export type FetchCUIDataResult = {
  teams?: FetchTeamsResult;
  sport?: FetchResult;
  competitions?: FetchResult;
  competitionType?: FetchResult;
  stop?: () => void;
};

// Conditional request state per resource
const etags: Partial<Record<ResourceKey, string | null>> = {};
const lastMods: Partial<Record<ResourceKey, string | null>> = {};
let pollTimer: ReturnType<typeof setInterval> | null = null;

const resourcePaths: Record<Exclude<ResourceKey, "teams">, string> = {
  sport: "/sport.json",
  competitions: "/competitions.json",
  competitionType: "/competitionType.json",
};

// [Fix #1] Validators check for non-array objects with at least one key whose value has a `name` field
const validators: Record<Exclude<ResourceKey, "teams">, (d: unknown) => boolean> = {
  sport: (d) => isNonEmptyRecord(d) && hasNamedEntries(d as Record<string, unknown>),
  competitions: (d) => isNonEmptyRecord(d) && hasNamedEntries(d as Record<string, unknown>),
  competitionType: (d) => isNonEmptyRecord(d) && hasNamedEntries(d as Record<string, unknown>),
};

function isNonEmptyRecord(d: unknown): boolean {
  return !!d && typeof d === "object" && !Array.isArray(d) && Object.keys(d as object).length > 0;
}

function hasNamedEntries(d: Record<string, unknown>): boolean {
  const firstValue = Object.values(d)[0];
  return !!firstValue && typeof firstValue === "object" && "name" in (firstValue as object);
}

const parsers: Record<Exclude<ResourceKey, "teams">, (d: unknown) => Record<string, unknown>> = {
  sport: (d) => parseSportData(d as SportJsonInput),
  competitions: (d) => parseCompetitionsData(d as CompetitionsJsonInput),
  competitionType: (d) => parseCompetitionTypeData(d as CompetitionTypeJsonInput),
};

const setters: Record<Exclude<ResourceKey, "teams">, (d: unknown) => void> = {
  sport: (d) => setSportsMap(d as ReturnType<typeof parseSportData>),
  competitions: (d) => setCompetitionsMap(d as ReturnType<typeof parseCompetitionsData>),
  competitionType: (d) => setCompetitionTypeMap(d as ReturnType<typeof parseCompetitionTypeData>),
};

// [Fix #4] Validate baseUrl is https
function isValidBaseUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:";
  } catch {
    return false;
  }
}

async function fetchResource(key: Exclude<ResourceKey, "teams">, baseUrl: string, forceUpdate: boolean): Promise<FetchResult> {
  const url = `${baseUrl}${resourcePaths[key]}`;
  try {
    const isDefault = baseUrl === BASE_URL;
    const headers: Record<string, string> = isDefault ? { ...DEFAULT_HEADERS as Record<string, string> } : {};

    // [Fix #5] Clear stale conditional headers on forceUpdate
    if (forceUpdate) {
      etags[key] = null;
      lastMods[key] = null;
    } else {
      if (etags[key]) headers["If-None-Match"] = etags[key]!;
      if (lastMods[key]) headers["If-Modified-Since"] = lastMods[key]!;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT);
    let response: Response;
    try {
      response = await fetch(url, { headers, signal: controller.signal });
    } catch (err) {
      return { success: false, updated: false, error: err instanceof Error && err.name === "AbortError" ? "Request timed out" : "Network error" };
    } finally {
      clearTimeout(timeoutId);
    }

    if (response.status === 304) return { success: true, updated: false };
    if (!response.ok) return { success: false, updated: false, error: `HTTP ${response.status}: ${response.statusText}` };

    etags[key] = response.headers.get("ETag");
    lastMods[key] = response.headers.get("Last-Modified");

    let json: unknown;
    try { json = await response.json(); } catch { return { success: false, updated: false, error: "Invalid JSON response" }; }
    if (!validators[key](json)) return { success: false, updated: false, error: `Invalid ${key} data structure` };

    try {
      const parsed = parsers[key](json);
      // [Fix #2] Don't overwrite good data with empty parsed result
      if (Object.keys(parsed).length === 0) {
        return { success: false, updated: false, error: `Parsed ${key} data is empty` };
      }
      setters[key](parsed);
    } catch {
      return { success: false, updated: false, error: `Failed to parse ${key} data` };
    }

    return { success: true, updated: true };
  } catch (error) {
    return { success: false, updated: false, error: error instanceof Error ? error.message : "Unknown error" };
  }
}

async function doFetchAll(resources: ResourceKey[], baseUrl: string, forceUpdate: boolean): Promise<FetchCUIDataResult> {
  const result: FetchCUIDataResult = {};
  const promises: Promise<void>[] = [];

  if (resources.includes("teams")) {
    promises.push(
      fetchTeams({ endpoint: `${baseUrl}/teams.json`, forceUpdate }).then((r) => { result.teams = r; })
    );
  }

  for (const key of resources) {
    if (key === "teams") continue;
    promises.push(
      fetchResource(key, baseUrl, forceUpdate).then((r) => { result[key] = r; })
    );
  }

  await Promise.all(promises);
  return result;
}

/**
 * Fetches CUI data (teams, sport, competitions, competitionType) from S3
 * and stores parsed results on `window.CUI` for use by getter functions
 * (`getSportInfo`, `getCompetitionInfo`, `getCompetitionTypeInfo`, `getTeamInfo`).
 *
 * Uses conditional requests (ETag / If-Modified-Since) to avoid re-downloading
 * unchanged data. Each resource is fetched in parallel via `Promise.all`.
 *
 * @param {FetchCUIDataOptions} [options] - Configuration options
 * @param {Array<"teams"|"sport"|"competitions"|"competitionType">} [options.resources] -
 *   Which resources to fetch. Defaults to all four. Pass a subset to fetch selectively.
 * @param {string} [options.baseUrl] - Base URL for JSON endpoints. Must be HTTPS.
 *   Defaults to `https://api.americanwagering.com/caesars-ui-assets/json`.
 *   Resource paths (`/teams.json`, `/sport.json`, etc.) are appended automatically.
 * @param {boolean} [options.forceUpdate=false] - Skip conditional request headers and
 *   version checks, forcing a full re-fetch and re-parse.
 * @param {boolean} [options.polling=false] - When true, re-fetches on an interval.
 *   The returned result will include a `stop()` function to cancel polling.
 * @param {number} [options.interval=86400000] - Polling interval in ms (default 24h, min 5s).
 *
 * @returns {Promise<FetchCUIDataResult>} Result object with per-resource outcomes:
 *   - `teams`, `sport`, `competitions`, `competitionType` — each has `{ success, updated, error? }`
 *   - `stop` — function to cancel polling (only present when `polling: true`)
 *
 * @example
 * // Fetch all resources once
 * const result = await fetchCUIData();
 *
 * @example
 * // Fetch only sport and competitions
 * const result = await fetchCUIData({ resources: ["sport", "competitions"] });
 *
 * @example
 * // Poll every 60 seconds, stop later
 * const result = await fetchCUIData({ polling: true, interval: 60000 });
 * // ... later
 * result.stop();
 *
 * @example
 * // Force re-fetch ignoring cache
 * const result = await fetchCUIData({ forceUpdate: true });
 *
 * @example
 * // Custom endpoint
 * const result = await fetchCUIData({ baseUrl: "https://my-cdn.com/cui-assets" });
 */
export async function fetchCUIData(options?: FetchCUIDataOptions): Promise<FetchCUIDataResult> {
  const {
    resources = ["teams", "sport", "competitions", "competitionType"],
    baseUrl = BASE_URL,
    forceUpdate = false,
    polling = false,
    interval = DEFAULT_POLL_INTERVAL,
  } = options ?? {};

  // [Fix #4] Reject non-HTTPS base URLs
  if (!isValidBaseUrl(baseUrl)) {
    return { teams: undefined, sport: undefined, competitions: undefined, competitionType: undefined, stop: undefined };
  }

  const safeInterval = Math.max(interval, MIN_POLL_INTERVAL);
  const result = await doFetchAll(resources, baseUrl, forceUpdate);

  if (polling) {
    stopCUIDataPolling();
    pollTimer = setInterval(() => {
      // [Fix #7] No console.log in polling — fire and forget
      doFetchAll(resources, baseUrl, false);
    }, safeInterval);
    result.stop = stopCUIDataPolling;
  }

  return result;
}

/** Stops all CUI data polling (including teams) */
export function stopCUIDataPolling(): void {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  stopTeamsPolling();
}
